// Class templates as data (decision 0050: "classes and jobs: none in spike 002; class templates as data (starting
// stats, growth, allowed gear) in the engine"). A template names starting values for the stat set, growth per level
// that replaces or adds to the progression's, the abilities and effects it starts with, and the gear it may use (item
// tags). Spike 002 loads and validates its templates and uses none; a game that plays classes builds a character's
// ability state with `classStart` and its progression with `classProgression`.
import type { AbilitySet, AbilityState } from "./system";
import type { ProgressionDef } from "./progression";

/** One class template. */
export interface ClassTemplate {
  id: string;
  /** Starting values by stat (the stat set's bases otherwise). */
  start?: Readonly<Record<string, number>>;
  /** Growth per level by stat: added to the progression's (`growthMode: "add"`, the default) or in its place. */
  growth?: Readonly<Record<string, number>>;
  growthMode?: "add" | "replace";
  /** Abilities it may activate from the start (the game's ability ids). */
  abilities?: readonly string[];
  /** Effects it starts with (infinite ones: a passive). */
  effects?: readonly string[];
  /** The gear it may use: item tags, any of which allows an item. Absent: any. */
  gear?: readonly string[];
}

/** What a template is checked against: the stat set and, when known, the ability, effect and item-tag ids. */
export interface ClassContext {
  attributes: readonly string[];
  abilities?: readonly string[];
  effects?: readonly string[];
  itemTags?: readonly string[];
}

/** Problems with class templates (an unknown stat, ability, effect or gear tag; a duplicate id; a bad number).
 *  Empty when sound. */
export function checkClassTemplates(list: readonly ClassTemplate[], ctx: ClassContext): string[] {
  const out: string[] = [];
  const stats = new Set(ctx.attributes);
  const seen = new Set<string>();
  for (const c of list) {
    const at = `class "${c.id}"`;
    if (!c.id) out.push("a class has no id");
    if (seen.has(c.id)) out.push(`${at} is defined twice`);
    seen.add(c.id);
    for (const [s, v] of Object.entries(c.start ?? {})) {
      if (!stats.has(s)) out.push(`${at} starts unknown stat "${s}"`);
      if (!Number.isFinite(v)) out.push(`${at} starts "${s}" at ${v}`);
    }
    for (const [s, v] of Object.entries(c.growth ?? {})) {
      if (!stats.has(s)) out.push(`${at} grows unknown stat "${s}"`);
      if (!Number.isFinite(v)) out.push(`${at} grows "${s}" by ${v}`);
    }
    if (ctx.abilities) for (const a of c.abilities ?? []) if (!ctx.abilities.includes(a)) out.push(`${at} names unknown ability "${a}"`);
    if (ctx.effects) for (const e of c.effects ?? []) if (!ctx.effects.includes(e)) out.push(`${at} names unknown effect "${e}"`);
    if (ctx.itemTags) for (const g of c.gear ?? []) if (!ctx.itemTags.includes(g)) out.push(`${at} allows unknown gear tag "${g}"`);
  }
  return out;
}

/** A character's starting ability state for a template: the stat set at the template's starting values, with its
 *  starting effects applied. Throws on an unknown stat or effect. */
export function classStart(set: AbilitySet, c: ClassTemplate): AbilityState {
  const s = set.init({ ...(c.start ?? {}) });
  for (const e of c.effects ?? []) {
    const r = set.apply(s, e);
    if ("refused" in r) throw new Error(`class "${c.id}": its effect "${e}" was refused (${r.refused})`);
  }
  return s;
}

/** The progression a template plays with: its growth added to (or in place of) the definition's. */
export function classProgression(def: ProgressionDef, c: ClassTemplate): ProgressionDef {
  if (!c.growth) return def;
  if (c.growthMode === "replace") return { ...def, growth: { ...c.growth } };
  const growth: Record<string, number> = { ...def.growth };
  for (const [s, v] of Object.entries(c.growth)) growth[s] = (growth[s] ?? 0) + v;
  return { ...def, growth };
}

/** Whether a template may use an item with these tags. */
export const classAllows = (c: ClassTemplate, itemTags: readonly string[]): boolean => !c.gear || c.gear.some((g) => itemTags.includes(g));
